import { useState } from "react";
import { UnreadBadge } from "@/components/notifications/UnreadBadge";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrentPageTitle } from "@/contexts/PageTitleContext";
import { useNotifications } from "@/hooks/useNotifications";
import { PeritoCompanySwitcher } from "@/components/perito/PeritoCompanySwitcher";
import { PeritoProfileSheet } from "./PeritoProfileSheet";
import {
  HEADER_GRADIENT,
  TOPBAR_BACK_BUTTON_CLASS,
  TOPBAR_ROW_CLASS,
  TOPBAR_SHELL_CLASS,
  TOPBAR_TITLE_CLASS,
  TOPBAR_TOP_GAP,
} from "@/components/shell/MobileTopBar";
import { ImgOffline } from "@/components/offline/ImgOffline";

/**
 * Cabecera móvil del portal del perito — las mismas variantes que la de
 * admin/manager (`shell/MobileTopBar`), copiadas al detalle.
 *
 * Dos cosas que antes no hacía y por eso «no era la misma»:
 *  1. Las páginas principales usan la variante ALTA: el hueco de arriba con
 *     suelo, `pb-4`, radio inferior 24, los dos globos decorativos y el
 *     título a la izquierda en 19px. Con la variante estrecha la cabecera del
 *     perito se veía bastante más baja que la del manager.
 *  2. Ahora respeta `mobileHeaderMode`, `mobileTitle` y sobre todo
 *     `mobileActions`. Sin eso, la ficha de producto —que define un menú
 *     distinto para móvil y para escritorio— le daba al perito el de
 *     escritorio, cuyas dos opciones son solo para quien puede gestionar: el
 *     menú de los tres puntos se abría VACÍO.
 *
 * Y «copiadas al detalle» ya no es literal: la carcasa, la fila, el título y
 * el hueco de arriba se IMPORTAN de `shell/MobileTopBar`. Estaban duplicados
 * carácter a carácter en los dos ficheros —y en tres ramas dentro de cada
 * uno—, que es exactamente como se habían separado antes sin que nadie lo
 * notara. Si allí cambia el alto, aquí cambia solo.
 */
export function PeritoTopBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile, user, linkedCompanies, switchCompany } = useAuth();
  const {
    title,
    actions,
    leftActions,
    mobileHeaderMode,
    onMobileBack,
    hideMobileTopBar,
    mobileActions,
    mobileTitle,
  } = useCurrentPageTitle();
  const { unreadCount } = useNotifications();
  const [profileOpen, setProfileOpen] = useState(false);

  if (hideMobileTopBar) return null;

  const fullName = profile?.full_name || user?.user_metadata?.full_name || user?.email || "P";
  const initial = (fullName || "P").charAt(0).toUpperCase();
  const avatarUrl = profile?.avatar_url ?? null;
  const hasUnread = (unreadCount ?? 0) > 0;
  const displayTitle = mobileTitle ?? title ?? t("perito.portal");

  /* EN QUÉ EMPRESA ESTÁ, EN TODAS LAS PANTALLAS (tanda 5, A9 / T01-04).
     Antes el conmutador solo se pintaba si la página no tenía botones propios en
     la cabecera, así que en Sanidad, Planificador y Notificaciones el perito no
     veía en qué empresa estaba ni podía cambiarla. Ahora va debajo del título,
     en texto pequeño, en las tres variantes: no compite con los botones y no
     cambia la altura de la banda (título y línea caben en los 40 px del avatar). */
  const companySwitcher = linkedCompanies.length > 1 ? (
    <PeritoCompanySwitcher
      companies={linkedCompanies}
      currentCompanyId={profile?.company_id || null}
      onSwitch={switchCompany}
      variant="inline"
    />
  ) : null;
  const titulo = companySwitcher ? (
    <div className="flex-1 min-w-0">
      <h1 className={`${TOPBAR_TITLE_CLASS} block leading-tight`}>{displayTitle}</h1>
      {companySwitcher}
    </div>
  ) : (
    <h1 className={TOPBAR_TITLE_CLASS}>{displayTitle}</h1>
  );

  // ---------- SUB-TALL: flecha atrás + título, en la misma caja que la alta ----------
  // El título va a la izquierda, no centrado: la flecha de atrás ocupa el sitio
  // que dejaba el avatar, así que entrar en una subpágina ya no mueve el texto.
  if (mobileHeaderMode === "sub-tall") {
    return (
      <header
        className={TOPBAR_SHELL_CLASS}
        style={{
          paddingTop: TOPBAR_TOP_GAP,
          background: HEADER_GRADIENT,
        }}
      >
        <div className={TOPBAR_ROW_CLASS}>
          <button
            type="button"
            onClick={() => (onMobileBack ? onMobileBack() : navigate(-1))}
            className={TOPBAR_BACK_BUTTON_CLASS}
            aria-label={t("common.back")}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          {titulo}
          <div className="flex items-center gap-2 flex-shrink-0">{mobileActions ?? actions}</div>
        </div>
      </header>
    );
  }

  // ---------- BACK-ONLY: igual que sub-tall pero sin caer a navigate(-1) ----------
  if (mobileHeaderMode === "back-only") {
    return (
      <header
        className={TOPBAR_SHELL_CLASS}
        style={{
          paddingTop: TOPBAR_TOP_GAP,
          background: HEADER_GRADIENT,
        }}
      >
        <div className={TOPBAR_ROW_CLASS}>
          <button
            type="button"
            onClick={() => onMobileBack?.()}
            className={TOPBAR_BACK_BUTTON_CLASS}
            aria-label={t("common.back")}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          {titulo}
          <div className="flex items-center gap-2 flex-shrink-0">{mobileActions ?? actions}</div>
        </div>
      </header>
    );
  }

  // ---------- ALTA (por defecto en las páginas principales) ----------
  return (
    <>
      <header
        className={TOPBAR_SHELL_CLASS}
        style={{
          paddingTop: TOPBAR_TOP_GAP,
          background: HEADER_GRADIENT,
        }}
      >
        <div aria-hidden className="absolute -right-12 -top-8 w-40 h-40 rounded-full bg-white/10" />
        <div aria-hidden className="absolute -right-20 top-10 w-36 h-36 rounded-full bg-white/5" />

        <div className={TOPBAR_ROW_CLASS}>
          <button
            onClick={() => setProfileOpen(true)}
            className="relative w-10 h-10 rounded-full bg-white/[0.18] backdrop-blur-md flex items-center justify-center text-white font-bold text-base ring-1 ring-white/40 active:scale-95 transition-transform flex-shrink-0"
            aria-label={t("nav.profile", "Profile")}
          >
            {avatarUrl ? (
              <ImgOffline src={avatarUrl} alt={fullName} className="w-full h-full object-cover rounded-full" />
            ) : (
              initial
            )}
            {hasUnread && <UnreadBadge count={unreadCount} />}
          </button>

          {titulo}

          <div className="flex items-center gap-2 flex-shrink-0">
            {mobileActions ?? actions}
          </div>
        </div>

        {leftActions && (
          <div className="relative flex items-center gap-2 mt-3">{leftActions}</div>
        )}
      </header>

      <PeritoProfileSheet open={profileOpen} onOpenChange={setProfileOpen} />
    </>
  );
}
